import { z } from 'zod';
import { v4 as uuid } from 'uuid';
import { assign } from 'lodash';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Output, signal, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import { DragulaService } from "ng2-dragula";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import * as FlatServices from '@medsurf/flat-services';
import { ZodSharedValidator } from '@medsurf/flat-validators';
import { DragContainerComponent } from "@medsurf/flat-components";

@Component({
    selector: 'medsurf-common-media-library-media-sidebar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './media-sidebar.component.html',
    styleUrls: ['./media-sidebar.component.scss'],
    standalone: false
})
export class MediaSidebarComponent extends DragContainerComponent {
  @Output() mediaChange = new EventEmitter();
  
  @ViewChild('videoPlayer') videoPlayer: ElementRef<HTMLVideoElement>;
  
  #isSubmitted = signal(false);
  uuid: string;
  media: FlatModels.MediaEntityModels.MediaEntityType | null;
  mediaForm = new FormGroup(
    {
      name: new FormControl(''),
      altText: new FormControl(''),
      access: new FormControl(''),
      compoundNumber: new FormControl(''),
      stain: new FormControl(''),
      authorNote: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.MediaEntityModels.MediaSchema.pick({
          name: true,
          altText: true,
          access: true,
          compoundNumber: true,
          stain: true,
          authorNote: true,
        })
      ),
      updateOn: 'change',
    }
  );
  copyrightForm = new FormGroup(
    {
      id: new FormControl<string | null>(null)
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.CopyrightEntityModels.AddCopyrightSchema,
      ),
      updateOn: 'change',
    }
  );
  mediaDimensionForm = new FormGroup(
    {
      pixelPerMillimeter: new FormControl(0),
      depth: new FormControl<string | null>(null)
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.DimensionSharedModels.DimensionsSchema.pick({
          pixelPerMillimeter: true,
          depth: true
        })
      ),
      updateOn: 'change',
    }
  );
  mediaPositionForm = new FormGroup(
    {
      copyrightPosition: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        z.object({
          copyrightPosition: FlatModels.MediaEntityModels.CopyrightPosition.nullable()
        })
      ),
      updateOn: 'change'
    }
  );
  readonly FlatModels = FlatModels;
  
  constructor(protected mediaAuthorFacade: FlatFacades.MediaAuthorFacade,
              protected constantAuthorFacade: FlatFacades.ConstantAuthorFacade,
              protected searchControlFacade: FlatFacades.SearchControlFacade,
              protected mediaControlService: FlatServices.MediaControlService,
              private alertControlFacade: FlatFacades.AlertControlFacade,
              private apiControlService: FlatServices.ApiControlService,
              public clipboardAuthorFacade: FlatFacades.ClipboardAuthorFacade,
              dragulaService: DragulaService) {
    super(dragulaService);
    this.constantAuthorFacade.requestGetStains().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
    this.constantAuthorFacade.requestGetCopyrights().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
    
    // Update Current Selected Media
    this.searchControlFacade.currentSelectedMedia$.pipe(takeUntilDestroyed()).subscribe((media) => {
      this.media = structuredClone(media);
      if (this.media) {
        this.mediaForm.reset(this.media);
        if (this.media.type === FlatModels.MediaEntityModels.MediaType.enum.MediaDeepzoom) {
          this.mediaDimensionForm.reset({
            pixelPerMillimeter: (this.media as FlatModels.MediaEntityModels.MediaDeepzoom).dimensions?.pixelPerMillimeter,
            depth: (this.media as FlatModels.MediaEntityModels.MediaDeepzoom).dimensions?.depth,
          });
        }
      }
      this.#isSubmitted.set(false);
    });
  }
  
  onSubmit() {
    if (this.#isSubmitted() || this.mediaForm.invalid || !this.media) {
      return;
    }
    this.#isSubmitted.set(true);
    // Update media
    const media = assign(this.media, this.mediaForm.value);
    this.mediaAuthorFacade.requestUpdateMedia(media).subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.mediaForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
        this.alertControlFacade.requestAddAlert({
          key: 'main',
          detail: 'notification.saved',
          severity: FlatModels.AlertControlModels.AlertType.enum.success,
          life: FlatModels.AlertControlModels.AlertTime.DEFAULT
        });
        this.mediaChange.emit();
      }
    });
  }
  
  onCopyrightSubmit() {
    if (this.#isSubmitted() || this.copyrightForm.invalid || !this.media || !this.copyrightForm.value.id) {
      return;
    }
    this.#isSubmitted.set(true);
    // Update media
    const media = this.media;
    if (!media.mediaCopyrights) {
      media.mediaCopyrights = [];
    }
    media.mediaCopyrights.push({
      id: uuid(),
      media: this.media.id,
      copyright: this.copyrightForm.value.id,
      order: media.mediaCopyrights.length,
      text: '',
    });
    
    this.mediaAuthorFacade.requestUpdateMedia(media).subscribe({
      error: (result) => {
        this.alertControlFacade.requestAddError(result);
        this.copyrightForm.reset();
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.alertControlFacade.requestAddAlert({
          key: 'main',
          detail: 'notification.saved',
          severity: FlatModels.AlertControlModels.AlertType.enum.success,
          life: FlatModels.AlertControlModels.AlertTime.DEFAULT
        });
        this.copyrightForm.reset();
        this.mediaChange.emit();
        this.#isSubmitted.set(false);
      }
    });
  }
  
  onDimensionSubmit() {
    if (this.#isSubmitted() || this.mediaDimensionForm.invalid || !this.media
      || !([FlatModels.MediaEntityModels.MediaType.enum.MediaImage,
        FlatModels.MediaEntityModels.MediaType.enum.MediaDeepzoom,
        FlatModels.MediaEntityModels.MediaType.enum.MediaVideo] as string[]).includes(this.media.type)) {
      return;
    }
    this.#isSubmitted.set(true);
    // Update Media
    const media = structuredClone(this.media) as FlatModels.MediaEntityModels.MediaImage;
    if (!media.dimensions) {
      media.dimensions = {
        width: null,
        height: null,
        numTiles: null,
        tileSize: null,
        pixelPerMillimeter: null,
        depth: null
      };
    }
    media.dimensions.pixelPerMillimeter = this.mediaDimensionForm.value.pixelPerMillimeter || 0;
    media.dimensions.depth = this.mediaDimensionForm.value.depth || '';

    this.mediaAuthorFacade.requestUpdateMedia(media).subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.mediaDimensionForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
        this.alertControlFacade.requestAddAlert({
          key: 'main',
          detail: 'notification.saved',
          severity: FlatModels.AlertControlModels.AlertType.enum.success,
          life: FlatModels.AlertControlModels.AlertTime.DEFAULT
        });
        this.mediaChange.emit();
      }
    });
  }
  
  onPositionSubmit() {
    if (this.#isSubmitted() || this.mediaPositionForm.invalid || !this.media || this.mediaPositionForm.value.copyrightPosition === null) {
      return;
    }
    this.#isSubmitted.set(true);
    this.mediaAuthorFacade.requestUpdateWatermark(this.media, this.mediaPositionForm.value.copyrightPosition as FlatModels.MediaEntityModels.CopyrightPosition).subscribe({
      error: (result) => {
        this.alertControlFacade.requestAddError(result);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.alertControlFacade.requestAddAlert({
          key: 'main',
          detail: 'notification.saved',
          severity: FlatModels.AlertControlModels.AlertType.enum.success,
          life: FlatModels.AlertControlModels.AlertTime.DEFAULT
        });
        this.mediaPositionForm.reset();
        this.mediaChange.emit();
        this.#isSubmitted.set(false);
      }
    });
  }
  
  uploadPoster() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.jpg, .jpeg, .png';
    input.addEventListener('change', async () => {
      if (input.files && input.files.length > 0) {
        const file = input.files[0];
        this.apiControlService.createMediaUpload({
          file,
          action: 'uploadVideoPoster',
          mediaId: this.media?.id,
          onError: (error) => this.alertControlFacade.requestAddError(error)
        })
      }
    });
    input.click();
  }
  
  async uploadPosterFromVideoFrame() {
    if (!this.media) return;
    const video = this.videoPlayer.nativeElement;
    const canvas =  document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob: Blob | null = await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 80);
    });
    if (!blob) return;
    const file = new File([blob], 'video_frame', { type: blob.type });

    this.apiControlService.createMediaUpload({
      file,
      action: 'uploadVideoPoster',
      mediaId: this.media?.id,
      onError: (error) => this.alertControlFacade.requestAddError(error)
    })
  }
  
  mediaCopyrightTrackBy(index: number,
                        item: FlatModels.MediaEntityModels.MediaCopyright){
    return item.id || index;
  }
  
  trackByIds(index: number,
             item: any) {
    return item[0] || index;
  }
  
  onMediaCopyrightMoved(mediaCopyrights: FlatModels.MediaEntityModels.MediaCopyright[]) {
    if (!this.media) return;
    this.mediaAuthorFacade.requestRearrangeMediaCopyrights(this.media, mediaCopyrights).subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.mediaForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
        this.alertControlFacade.requestAddAlert({
          key: 'main',
          detail: 'notification.saved',
          severity: FlatModels.AlertControlModels.AlertType.enum.success,
          life: FlatModels.AlertControlModels.AlertTime.DEFAULT
        });
        this.mediaChange.emit();
      }
    });
  }
}
